import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { gsap } from 'gsap';
import { useTranslation } from 'react-i18next';

/**
 * TestimonialsSection component for the homepage
 * Displays client testimonials in an elegant carousel
 * Designed with a minimalistic black and white approach
 * Supports multiple languages through i18n
 */
const TestimonialsSection = () => {
  const { t } = useTranslation();
  const sectionRef = useRef(null);
  const titleRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.1 });
  
  // Testimonial data
  const testimonials = [
    {
      quote: "Nathan and Vincent did an excellent job translating Carried Away and its store page. They reacted quickly to modifications and met tight deadlines. We will definitely work with them again!",
      author: "Jonny",
      company: "Huge Calf Studios",
      game: "Carried Away"
    },
    {
      quote: "Very professional team! They helped us translate our game perfectly. Communication was great. Highly recommended!",
      author: "TwoMammoths",
      company: "Developers of Archaica",
      game: "Archaica: The Path of Light"
    },
    {
      quote: "Our game got translated faster than expected! We're extremely happy with the results and excited to introduce our game to a new audience.",
      author: "Christian Wasser",
      company: "Developer of BlobCat",
      game: "BlobCat"
    },
    {
      quote: "Quick and high-quality French translation. Our French players now enjoy the game even more!",
      author: "Elementals Reborn Team",
      company: "",
      game: "Elementals Reborn"
    },
    {
      quote: "Vincent has been fantastic at translating Startup Company into French. He's always ready to translate new strings, and I have never received a complaint about the translation from players. Highly recommended!",
      author: "Jonas Hovgaard",
      company: "Developer of Startup Company",
      game: "Startup Company"
    },
    {
      quote: "Best French translators you will ever find. Fast and professional! I've noticed a 21% increase in French players after the translation.",
      author: "Sami",
      company: "Protection Games",
      game: ""
    }
  ];

  // State for current testimonial
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [direction, setDirection] = useState(1); // 1 for next, -1 for prev

  // GSAP animations
  useEffect(() => {
    if (isInView) {
      const ctx = gsap.context(() => {
        // Animate the title elements
        gsap.from(titleRef.current.querySelectorAll('div, h2, p'), {
          y: 30,
          opacity: 0,
          duration: 1,
          stagger: 0.2,
          ease: "power3.out"
        });
      }, sectionRef);
      
      return () => ctx.revert();
    }
  }, [isInView]);

  // Auto-rotate testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      if (!isAnimating) {
        goToNext();
      }
    }, 10000);
    
    return () => clearInterval(interval);
  }, [currentIndex, isAnimating]);

  // Navigation functions
  const goToNext = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setDirection(1);
    setCurrentIndex((prevIndex) => (prevIndex + 1) % testimonials.length);
    setTimeout(() => setIsAnimating(false), 700);
  };

  const goToPrev = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setDirection(-1);
    setCurrentIndex((prevIndex) => (prevIndex - 1 + testimonials.length) % testimonials.length);
    setTimeout(() => setIsAnimating(false), 700);
  };

  const goToIndex = (index) => {
    if (isAnimating || index === currentIndex) return;
    setIsAnimating(true);
    setDirection(index > currentIndex ? 1 : -1);
    setCurrentIndex(index);
    setTimeout(() => setIsAnimating(false), 700);
  };

  // Animation variants
  const titleVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.8,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  };

  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 100 : -100,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1,
      transition: {
        x: { type: "spring", stiffness: 300, damping: 30 },
        opacity: { duration: 0.4 }
      }
    },
    exit: (direction) => ({
      x: direction > 0 ? -100 : 100,
      opacity: 0,
      transition: {
        x: { type: "spring", stiffness: 300, damping: 30 },
        opacity: { duration: 0.4 }
      }
    })
  };

  return (
    <section ref={sectionRef} className="py-32 bg-white scroll-section relative">
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-1/3 h-px bg-primary/10"></div>
      
      <div className="container mx-auto px-6">
        <motion.div 
          ref={titleRef}
          className="max-w-3xl mx-auto mb-24 text-center"
          variants={titleVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          <div className="w-16 h-px bg-primary mx-auto mb-12"></div>
          <h2 className="font-heading text-3xl md:text-5xl font-normal mb-8 tracking-wide">
            {t('testimonials.title')}
          </h2>
          <p className="text-light text-lg md:text-xl font-light leading-relaxed max-w-2xl mx-auto">
            {t('testimonials.description')}
          </p>
        </motion.div>

        <div className="max-w-4xl mx-auto relative">
          {/* Testimonial Carousel */}
          <div className="relative overflow-hidden min-h-[300px]">
            <AnimatePresence initial={false} custom={direction} mode="wait">
              <motion.div
                key={currentIndex}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                className="border border-primary/10 p-12 md:p-16"
              >
                {/* Quote icon */}
                <div className="absolute top-10 left-10 text-7xl text-primary/5 font-heading">"</div>
                
                {/* Testimonial content */}
                <div className="relative z-10">
                  <p className="text-xl md:text-2xl text-primary font-light italic mb-10 leading-relaxed">
                    {testimonials[currentIndex].quote}
                  </p>
                  
                  <div>
                    <p className="font-medium text-primary text-lg tracking-wide">
                      {testimonials[currentIndex].author}
                      {testimonials[currentIndex].company && (
                        <span className="text-light font-light ml-1">— {testimonials[currentIndex].company}</span>
                      )}
                    </p>
                    {testimonials[currentIndex].game && (
                      <p className="text-light font-light mt-1">
                        {testimonials[currentIndex].game}
                      </p>
                    )}
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Navigation arrows */}
          <button 
            onClick={goToPrev}
            className="absolute top-1/2 -left-4 md:-left-12 -translate-y-1/2 w-12 h-12 border border-primary/20 flex items-center justify-center text-primary hover:bg-primary hover:text-white transition-all duration-300 focus:outline-none"
            aria-label="Previous testimonial"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M15 19l-7-7 7-7"></path>
            </svg>
          </button>
          
          <button 
            onClick={goToNext}
            className="absolute top-1/2 -right-4 md:-right-12 -translate-y-1/2 w-12 h-12 border border-primary/20 flex items-center justify-center text-primary hover:bg-primary hover:text-white transition-all duration-300 focus:outline-none"
            aria-label="Next testimonial"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M9 5l7 7-7 7"></path>
            </svg>
          </button>
          
          {/* Dots navigation */}
          <div className="flex justify-center mt-12 space-x-3">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => goToIndex(index)}
                className={`w-2 h-2 transition-all duration-300 focus:outline-none ${
                  index === currentIndex ? 'bg-primary w-6' : 'bg-primary/20 hover:bg-primary/40'
                }`}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
